import React from 'react';
import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { BaseFormFieldProps } from './types';
import CategorySelector from '../selectors/CategorySelector';

export interface FormCategorySelectorProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<BaseFormFieldProps<TFieldValues>, 'label' | 'placeholder'> {
  placeholder?: string;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showAllOption?: boolean;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

const FormCategorySelector = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  isRequired = false,
  showErrorOnTouched = true,
  placeholder,
  label,
  variant = 'bordered',
  className,
  'aria-label': ariaLabel,
  showAllOption = false,
  showEmptyOption,
  isDisabled = false,
  ...rest
}: FormCategorySelectorProps<TFieldValues>) => {
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CategorySelector
          value={field.value}
          onChange={field.onChange}
          isRequired={isRequired}
          isInvalid={showError}
          errorMessage={showError ? fieldError : undefined}
          placeholder={placeholder}
          label={label}
          variant={variant}
          className={className}
          aria-label={ariaLabel}
          showAllOption={showAllOption}
          showEmptyOption={showEmptyOption}
          isDisabled={isDisabled}
          {...rest}
        />
      )}
    />
  );
};

FormCategorySelector.displayName = 'FormCategorySelector';

export default FormCategorySelector;
